import Modal from '@ui-kit/components/modal';
import React, { ReactNode, useEffect, useState } from 'react';
import { container } from 'tsyringe';
import {
  AmazonPurchaseOptions,
  ITicketService,
  Price,
  PurchaseMethod,
  StripeIntent,
} from '../model/interfaces';
import styles from '../styles/buyTicketsModal.module.scss';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { ReactComponent as Lock } from '@ui-kit/icons/lock.svg';
import AmazonPayButton from './amazonButton';
import CreditCardForm from './creditCardForm';
import StripeForm from './stripeForm';
import classNames from 'classnames';
import PriceSelect from './priceSelect';
import PaymentOptionRadioButton from './paymentOptionRadioButton';
import PaymentProviderIcon from './paymentProviderIcon';
import { t } from '@base/utils/helpers';
import { IUserService } from '@base/features/user/model/interfaces';
import { useAsyncData } from '@utils/hooks';

export default function BuyTicketsModal(props: { methods?: PurchaseMethod[]; close: () => void }) {
  const [prices, setPrices] = useState<Price[]>([]);
  const [selectedMethod, setPurchaseMethod] = useState<PurchaseMethod>();
  const [price, setPrice] = useState<string>();
  const [paymentOptions, setPaymentOptions] = useState<string | AmazonPurchaseOptions | StripeIntent>();
  const ticketService: ITicketService = container.resolve('ticketsService');

  useEffect(() => {
    async function getPrices() {
      const prices = await ticketService.getPrices();
      setPrices(prices);
      setPrice(prices[1].price.toString());
    }
    getPrices();
  }, [ticketService]);

  useEffect(() => {
    if (!selectedMethod) return;
    purchase(selectedMethod);
  }, [selectedMethod, price]);

  async function purchase(purchaseMethod: PurchaseMethod) {
    if (!price) return;
    switch (purchaseMethod) {
      case PurchaseMethod.PAYPAL: {
        const url = await ticketService.paypalBuy(price);
        setPaymentOptions(url);
        break;
      }
      case PurchaseMethod.STRIPE: {
        const intent = await ticketService.stripeIntent(price);
        setPaymentOptions(intent);
        break;
      }
      case PurchaseMethod.AMAZON: {
        const options = await ticketService.amazonPurchase(price);
        setPaymentOptions(options);
        break;
      }
      case PurchaseMethod.CARD: {
        setPaymentOptions('card');
        break;
      }
      default:
        return;
    }
  }

  if (!props.methods)
    return (
      <Modal title="Sorry" closeModal={props.close} headerBtn>
        {t('ticket_purchasing_temp_unavailable')}
      </Modal>
    );

  return (
    <TicketsModal close={props.close}>
      <PriceSelection>
        <PriceSelect prices={prices} price={price} setPrice={setPrice} />
        <PurchaseInfo />
      </PriceSelection>
      <PurchaseOptionsList>
        {props.methods
          // Не показывать мобильную оплату
          .filter((option) => option !== PurchaseMethod.MOBILE)
          // Оплата картой всегда первая
          .sort((a, b) => (a === PurchaseMethod.CARD ? -1 : b === PurchaseMethod.CARD ? 1 : 0))
          .map((method) => (
            <div
              key={method}
              className={classNames(styles.option, {
                [styles.option_selected]: method === selectedMethod,
              })}
            >
              <header>
                <PaymentOptionRadioButton
                  value={method}
                  setValue={(purchaseMethod) => {
                    setPaymentOptions(undefined);
                    setPurchaseMethod(purchaseMethod);
                  }}
                />
                <PaymentProviderIcon option={method} />
              </header>
              {selectedMethod === method && price && paymentOptions && (
                <div className={styles.purchase}>
                  <Purchase
                    method={selectedMethod}
                    close={props.close}
                    paymentOptions={paymentOptions}
                    price={price}
                  />
                </div>
              )}
            </div>
          ))}
      </PurchaseOptionsList>
      <Footer />
    </TicketsModal>
  );
}

function TicketsModal(props: { children: ReactNode[]; close: () => void }) {
  return (
    <Modal
      title={t('buy_tickets')}
      classes={{ body: styles.buyTicketsModal }}
      closeModal={props.close}
      headerBtn
    >
      <header>{t('time_to_get_tickets')}</header>
      <main>{props.children}</main>
    </Modal>
  );
}

function PriceSelection(props: { children: ReactNode[] }) {
  return (
    <section>
      <h3>{t('select_amount_to_purchase')}</h3>
      <div className={styles.amount}>{props.children}</div>
    </section>
  );
}

function PurchaseOptionsList(props: { children: ReactNode[] }) {
  return (
    <section style={{ borderBottom: 'none', maxHeight: '40vh', overflow: 'auto' }}>
      <h3>{t('select_payment_method')}</h3>
      <div className={styles.purchaseOptions}>{props.children}</div>
    </section>
  );
}

function PurchaseInfo() {
  return (
    <ul>
      {/* <li>25% welcoming bouns with purchase up to 10 tickets</li> */}
      <li>{t('price_includes_transaction_charges')}</li>
      <li>{t('can_be_cashed_out')}</li>
    </ul>
  );
}

function Footer() {
  return (
    <footer>
      <span>
        <b>{t('please_note') + ': '}</b>
        {t('convert_tickets_into_cash')}
      </span>
      <span>
        <Lock />
        {t('secure_transaction')}
      </span>
    </footer>
  );
}

function Purchase(props: {
  method?: PurchaseMethod;
  paymentOptions: string | AmazonPurchaseOptions | StripeIntent;
  price: string;
  close: () => void;
}) {
  const userService: IUserService = container.resolve('userService');
  const ticketsService: ITicketService = container.resolve('ticketsService');
  const { data: personalInfo } = useAsyncData(() => userService.getUserInfo());
  const handleSuccess = async () => {
    if (personalInfo) {
      const actualTickets = await userService.getPartAccountInfo('tickets');
      if (actualTickets) ticketsService.accountBalance.next(actualTickets);
    }
    props.close();
  };

  switch (props.method) {
    case PurchaseMethod.STRIPE:
      return <StripeForm stripeIntent={props.paymentOptions as StripeIntent} onFinish={handleSuccess} />;
    case PurchaseMethod.PAYPAL:
      return (
        <PayPalButtons
          className={styles.paypal}
          style={{ shape: 'rect' }}
          forceReRender={[props.price]}
          onApprove={handleSuccess}
          createOrder={(_, actions) => {
            return actions.order.create({
              purchase_units: [{ amount: { value: props.price || '0' } }],
            });
          }}
        />
      );
    case PurchaseMethod.AMAZON:
      return (
        <AmazonPayButton options={props.paymentOptions as AmazonPurchaseOptions} onSuccess={handleSuccess} />
      );
    case PurchaseMethod.CARD:
      return <CreditCardForm price={props.price} onFinish={handleSuccess} />;
    default:
      return null;
  }
}
